'use client';

import { memo } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Button } from '../../components/button';
import { MetaDefault } from '../../components/typography';
import './copilot-progressing-state.css';

type ObjectEditProcessingOverlayProps = {
  ariaLabel: string;
  statusText: string;
  stopLabel: string;
  onStop: () => void;
  showWorkingLayer: boolean;
};

type CopilotProgressingDot = {
  x: number;
  y: number;
  delayMs: number;
  durationMs: number;
};

const STEP_PX = 12;
const CORNER_WIDTH_PX = 230;
const CORNER_HEIGHT_PX = 160;

function buildCopilotProgressingDots(seed: number): CopilotProgressingDot[] {
  const dots: CopilotProgressingDot[] = [];
  let n = 0;

  for (let y = 2; y < CORNER_HEIGHT_PX; y += STEP_PX) {
    for (let x = 2; x < CORNER_WIDTH_PX; x += STEP_PX) {
      const r = Math.abs(Math.sin(seed * ++n * 12.9898) * 43758.5453) % 1;

      dots.push({
        x,
        y,
        delayMs: Math.round(r * 1400),
        durationMs: Math.round(1400 + r * 1000),
      });
    }
  }

  return dots;
}

const COPILOT_PROGRESSING_TOP_LEFT_DOTS = buildCopilotProgressingDots(1.7);
const COPILOT_PROGRESSING_BOTTOM_RIGHT_DOTS = buildCopilotProgressingDots(3.1);

const CopilotProgressingCorner = memo(function CopilotProgressingCorner({
  corner,
  dots,
}: {
  corner: 'top-left' | 'bottom-right';
  dots: readonly CopilotProgressingDot[];
}) {
  return (
    <Box
      className={`copilot-progressing-corner copilot-progressing-corner--${corner}`}
    >
      {dots.map((dot) => (
        <Box
          key={`${dot.x}-${dot.y}-${dot.delayMs}`}
          as="span"
          className="copilot-progressing-dot"
          style={{
            left: `${dot.x}px`,
            top: `${dot.y}px`,
            animationDelay: `${dot.delayMs}ms`,
            animationDuration: `${dot.durationMs}ms`,
          }}
        />
      ))}
    </Box>
  );
});

export function ObjectEditProcessingOverlay({
  ariaLabel,
  statusText,
  stopLabel,
  onStop,
  showWorkingLayer,
}: ObjectEditProcessingOverlayProps) {
  return (
    <Box
      className="copilot-progressing-overlay"
      data-copilot-working-layer-visible={showWorkingLayer ? 'true' : 'false'}
      role="status"
      aria-label={ariaLabel}
    >
      {showWorkingLayer ? (
        <>
          <Box className="copilot-progressing-glow" />
          <CopilotProgressingCorner
            corner="top-left"
            dots={COPILOT_PROGRESSING_TOP_LEFT_DOTS}
          />
          <CopilotProgressingCorner
            corner="bottom-right"
            dots={COPILOT_PROGRESSING_BOTTOM_RIGHT_DOTS}
          />
        </>
      ) : null}
      <HStack
        className="copilot-progressing-indicator"
        position="absolute"
        left="50%"
        bottom={showWorkingLayer ? '14px' : '24px'}
        transform="translateX(-50%)"
        zIndex={9}
        gap={2}
        px={3}
        py={1.5}
        alignItems="center"
        whiteSpace="nowrap"
        borderRadius="full"
        bg="bg.tooltip"
        boxShadow="lg"
        pointerEvents="auto"
      >
        <Box as="span" className="copilot-progressing-indicator-dot" />
        <MetaDefault color="fg.tooltip.default">{statusText}</MetaDefault>
        <Box
          display="flex"
          alignItems="center"
          pl={2}
          borderLeftWidth="1px"
          borderColor="border.tooltip"
        >
          <Button
            variant="link"
            size="xs"
            color="fg.accent.muted"
            onClick={onStop}
          >
            {stopLabel}
          </Button>
        </Box>
      </HStack>
    </Box>
  );
}
